// Local-only storage (Firebase migration in progress)

export interface ExamSessionStats {
  id: string;
  mode: 'exam' | 'training';
  totalQuestions: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  score: number;
  durationSeconds: number;
  averageSecondsPerQuestion: number;
  completedAt: string;
  status?: 'in_progress' | 'completed';
}

export interface ExamStatsSummary {
  totalSessions: number;
  totalQuestions: number;
  totalCorrect: number;
  totalIncorrect: number;
  totalUnanswered: number;
  accuracy: number;
  averageSecondsPerQuestion: number;
  bestScore: number;
  latestScore: number;
  examSessions: number;
  trainingSessions: number;
  trend: 'up' | 'down' | 'stable' | 'new';
  sessions: ExamSessionStats[];
}

export interface QuestionAttempt {
  id: number;
  question_id: string;
  scenario_en: string;
  scenario_ar: string;
  options_en: string[];
  options_ar: string[];
  correct_answer: string | string[];
  selected_answer: string | string[];
  is_correct: boolean;
  time_spent_seconds: number;
  domain: string;
  difficulty: string;
  mode: 'exam' | 'training';
  attempted_at: string;
}

const BASE_STORAGE_KEY = 'pmp_exam_stats_v1';
const ATTEMPTS_KEY = 'pmp_question_attempts';

function getStorageKey(userId?: string | number | null) {
  if (!userId) return `${BASE_STORAGE_KEY}_anonymous`;
  return `${BASE_STORAGE_KEY}_uid_${userId}`;
}

export function saveExamSession(
  session: Omit<ExamSessionStats, 'id' | 'completedAt'>,
  userId?: string | number | null,
  inProgressSessionId?: string
) {
  const sessions = getExamSessions(userId);
  const completedAt = new Date().toISOString();
  const status = session.status === 'in_progress' ? 'in_progress' : 'completed';
  const sessionId = inProgressSessionId || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  let nextSessions: ExamSessionStats[];
  if (inProgressSessionId) {
    // update existing session
    nextSessions = sessions.map((s) =>
      s.id === inProgressSessionId
        ? { ...session, id: s.id, completedAt, status }
        : s
    );
  } else {
    const nextSession: ExamSessionStats = {
      ...session,
      id: sessionId,
      completedAt,
      status,
    };
    nextSessions = [nextSession, ...sessions];
  }

  localStorage.setItem(getStorageKey(userId), JSON.stringify(nextSessions.slice(0, 100)));

  return { id: sessionId, completedAt };
}

export function saveQuestionAttempt(
  attempt: Omit<QuestionAttempt, 'attempted_at'>,
  userId?: string | number | null
) {
  const attempts = getUserQuestionAttempts(userId);
  const nextAttempt: QuestionAttempt = {
    ...attempt,
    attempted_at: new Date().toISOString(),
  };
  const nextAttempts = [nextAttempt, ...attempts].slice(0, 500);
  localStorage.setItem(`${ATTEMPTS_KEY}_${userId || 'anonymous'}`, JSON.stringify(nextAttempts));
  return nextAttempt;
}

export function getExamSessions(userId?: string | number | null): ExamSessionStats[] {
  try {
    const key = getStorageKey(userId);
    const raw = localStorage.getItem(key);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getUserQuestionAttempts(userId?: string | number | null): QuestionAttempt[] {
  try {
    const key = `${ATTEMPTS_KEY}_${userId || 'anonymous'}`;
    const raw = localStorage.getItem(key);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getFailedQuestions(userId?: string | number | null): QuestionAttempt[] {
  const attempts = getUserQuestionAttempts(userId);
  // keep only the most recent attempt per question_id
  const latestByQuestion = new Map<string, QuestionAttempt>();
  attempts.forEach((a) => {
    const existing = latestByQuestion.get(a.question_id);
    if (!existing || new Date(a.attempted_at) > new Date(existing.attempted_at)) {
      latestByQuestion.set(a.question_id, a);
    }
  });
  return Array.from(latestByQuestion.values()).filter((a) => !a.is_correct);
}

export function getExamStatsSummary(userId?: string | number | null): ExamStatsSummary {
  const sessions = getExamSessions(userId);
  const attempts = getUserQuestionAttempts(userId);
  const totalSessions = sessions.length;
  const totalCorrect = sessions.reduce((sum, session) => sum + session.correct, 0);
  const totalIncorrect = sessions.reduce((sum, session) => sum + session.incorrect, 0);
  const totalUnanswered = sessions.reduce((sum, session) => sum + session.unanswered, 0);
  const totalDuration = sessions.reduce((sum, session) => sum + session.durationSeconds, 0);
  const bestScore = sessions.reduce((best, session) => Math.max(best, session.score), 0);
  const latestScore = sessions[0]?.score || 0;
  const previousScore = sessions[1]?.score;

  // Also compute from individual question attempts (covers training mode where user checks answers but doesn't finish)
  const latestByQuestion = new Map<string, QuestionAttempt>();
  attempts.forEach((a) => {
    const existing = latestByQuestion.get(a.question_id);
    if (!existing || new Date(a.attempted_at) > new Date(existing.attempted_at)) {
      latestByQuestion.set(a.question_id, a);
    }
  });
  const attemptCorrect = Array.from(latestByQuestion.values()).filter(a => a.is_correct).length;
  const attemptIncorrect = Array.from(latestByQuestion.values()).filter(a => !a.is_correct).length;
  const attemptTotalTime = attempts.reduce((sum, a) => sum + a.time_spent_seconds, 0);

  // Compute attempt-based score when no sessions exist
  const attemptTotal = attemptCorrect + attemptIncorrect;
  const attemptScore = attemptTotal > 0 ? Math.round((attemptCorrect / attemptTotal) * 100) : 0;

  // Count modes from attempts when sessions are empty
  const attemptExamCount = attempts.filter(a => a.mode === 'exam').length > 0 ? 1 : 0;
  const attemptTrainingCount = attempts.filter(a => a.mode === 'training').length > 0 ? 1 : 0;

  // Use the higher of session-based or attempt-based counts
  const finalCorrect = Math.max(totalCorrect, attemptCorrect);
  const finalIncorrect = Math.max(totalIncorrect, attemptIncorrect);
  const finalTotal = finalCorrect + finalIncorrect;
  const finalDuration = totalDuration > 0 ? totalDuration : attemptTotalTime;

  // Fall back to attempt-based stats when no sessions exist
  const finalBestScore = sessions.length > 0 ? bestScore : attemptScore;
  const finalLatestScore = sessions.length > 0 ? latestScore : attemptScore;
  const finalExamSessions = sessions.length > 0
    ? sessions.filter((session) => session.mode === 'exam').length
    : attemptExamCount;
  const finalTrainingSessions = sessions.length > 0
    ? sessions.filter((session) => session.mode === 'training').length
    : attemptTrainingCount;

  return {
    totalSessions: totalSessions > 0 ? totalSessions : (attemptTotal > 0 ? 1 : 0),
    totalQuestions: finalTotal,
    totalCorrect: finalCorrect,
    totalIncorrect: finalIncorrect,
    totalUnanswered,
    accuracy: finalTotal > 0 ? Math.round((finalCorrect / finalTotal) * 100) : 0,
    averageSecondsPerQuestion: finalTotal > 0 ? Math.round(finalDuration / finalTotal) : 0,
    bestScore: finalBestScore,
    latestScore: finalLatestScore,
    examSessions: finalExamSessions,
    trainingSessions: finalTrainingSessions,
    trend: previousScore === undefined ? (attemptTotal > 0 ? 'stable' : 'new') : latestScore > previousScore ? 'up' : latestScore < previousScore ? 'down' : 'stable',
    sessions,
  };
}

export function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}
